import { useState } from 'react'
import { ChevronDown, ChevronUp, Award, TrendingUp, Target, Download } from 'lucide-react'
import RadarChart from './RadarChart'
import { themesToDomains, calculateThemeScores } from '../utils/themeUtils'

/**
 * Assessment Summary Component
 * Displays comprehensive results with global score, theme breakdown, and detailed questions
 */
const AssessmentSummary = ({ clientData, assessmentData, onDownloadPDF, isDownloading }) => {
  const [expandedThemes, setExpandedThemes] = useState({})

  // 🛡️ DEFENSIVE: Validate assessmentData structure
  if (!assessmentData || !assessmentData.scores || !assessmentData.themes) {
    console.error('❌ AssessmentSummary: Invalid or missing assessmentData', assessmentData)
    
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-700 mb-4">
            Données d'évaluation manquantes
          </h2>
          <p className="text-red-600 mb-6">
            Les données de votre évaluation sont incomplètes ou corrompues.
            Cela peut arriver après une mise à jour ou si vous avez rafraîchi la page.
          </p>
          <button
            onClick={() => {
              // Clear all localStorage and restart
              localStorage.clear()
              window.location.href = '/'
            }}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            🔄 Recommencer une nouvelle évaluation
          </button>
        </div>
      </div>
    )
  }

  // Calculate global statistics
  const completedScores = Object.values(assessmentData.scores).filter(
    score => score !== null && score !== undefined
  )
  const averageScore = completedScores.length > 0
    ? completedScores.reduce((sum, score) => sum + score, 0) / completedScores.length
    : 0

  // Calculate theme averages
  const themeAverages = assessmentData.themes?.map(theme => {
    const themeQuestionScores = theme.questions
      .map(q => assessmentData.scores[q.id])
      .filter(score => score !== null && score !== undefined)
    
    return themeQuestionScores.length > 0
      ? themeQuestionScores.reduce((sum, score) => sum + score, 0) / themeQuestionScores.length
      : 0
  }) || []

  // Prepare data for radar chart
  const domains = themesToDomains(assessmentData.themes, 12) // Short names for chart
  const radarScores = calculateThemeScores(assessmentData.themes, assessmentData.scores)

  // Count strong and weak areas
  const strongThemes = themeAverages.filter(avg => avg >= 7).length
  const criticalThemes = themeAverages.filter(avg => avg < 5).length

  const toggleTheme = (themeId) => {
    setExpandedThemes(prev => ({
      ...prev,
      [themeId]: !prev[themeId]
    }))
  }

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100'
    if (score >= 7) return 'text-lime-600 bg-lime-100'
    if (score >= 5) return 'text-yellow-600 bg-yellow-100'
    if (score >= 3) return 'text-orange-600 bg-orange-100'
    return 'text-red-600 bg-red-100'
  }

  const getScoreBadge = (score) => {
    if (score >= 8) return { text: 'Excellent', color: 'bg-green-500' }
    if (score >= 7) return { text: 'Bon', color: 'bg-lime-500' }
    if (score >= 5) return { text: 'Moyen', color: 'bg-yellow-500' }
    if (score >= 3) return { text: 'À améliorer', color: 'bg-orange-500' }
    return { text: 'Critique', color: 'bg-red-500' }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-green-500 text-6xl mb-4">✅</div>
        <h2 className="text-3xl font-bold text-cedec-blue mb-2">
          Rapport envoyé avec succès !
        </h2>
        <p className="text-gray-600 mb-4">
          Votre rapport d'évaluation a été envoyé à <strong>{clientData?.email}</strong>
          {clientData?.consultantEmail && (
            <>
              {' '}et à votre consultant <strong>{clientData.consultantEmail}</strong>
            </>
          )}
        </p>
      </div>

      {/* Download Button - Prominent */}
      <div className="bg-gradient-to-r from-cedec-blue to-blue-600 rounded-xl shadow-lg p-6 text-white text-center">
        <h3 className="text-xl font-semibold mb-3 flex items-center justify-center gap-2">
          <Download className="w-6 h-6" />
          Télécharger votre rapport complet
        </h3>
        <p className="text-blue-100 text-sm mb-4">
          Conservez une copie PDF de votre évaluation détaillée
        </p>
        <button
          onClick={onDownloadPDF}
          disabled={isDownloading}
          className="inline-flex items-center gap-2 bg-white text-cedec-blue px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {isDownloading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cedec-blue"></div>
              Génération en cours...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              Télécharger le PDF
            </>
          )}
        </button>
      </div>

      {/* Global Statistics */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Award className="w-6 h-6 text-cedec-blue" />
          Vue d'ensemble de vos résultats
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Global Score */}
          <div className="bg-gradient-to-br from-cedec-blue to-blue-600 rounded-lg p-6 text-white text-center">
            <div className="text-4xl font-bold mb-2">{averageScore.toFixed(1)}</div>
            <div className="text-sm text-blue-100">Score global / 10</div>
            <div className={`mt-3 inline-block px-3 py-1 rounded-full text-xs font-semibold ${getScoreBadge(averageScore).color} text-white`}>
              {getScoreBadge(averageScore).text}
            </div>
          </div>

          {/* Completion Rate */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-cedec-blue mb-2">
              {Math.round((assessmentData.completedQuestions / assessmentData.totalQuestions) * 100)}%
            </div>
            <div className="text-sm text-gray-600">Taux de complétion</div>
            <div className="text-xs text-gray-500 mt-1">
              {assessmentData.completedQuestions}/{assessmentData.totalQuestions} questions
            </div>
          </div>

          {/* Strong Themes */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-green-600 mb-2 flex items-center justify-center gap-1">
              <TrendingUp className="w-6 h-6" />
              {strongThemes}
            </div>
            <div className="text-sm text-gray-600">Thèmes forts</div>
            <div className="text-xs text-gray-500 mt-1">Score ≥ 7/10</div>
          </div>

          {/* Areas to Improve */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2 flex items-center justify-center gap-1">
              <Target className="w-6 h-6" />
              {criticalThemes}
            </div>
            <div className="text-sm text-gray-600">Axes d'amélioration</div>
            <div className="text-xs text-gray-500 mt-1">Score &lt; 5/10</div>
          </div>
        </div>

        {/* Radar Chart */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-800 mb-4 text-center">
            Visualisation par thème
          </h4>
          <div className="max-w-md mx-auto">
            <RadarChart domains={domains} scores={radarScores} />
          </div>
        </div>
      </div>

      {/* Detailed Theme Breakdown */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">
          Détail par thème ({assessmentData.themes?.length || 0} thèmes évalués)
        </h3>

        <div className="space-y-4">
          {assessmentData.themes?.map((theme, themeIndex) => {
            const themeAverage = themeAverages[themeIndex]
            const isExpanded = expandedThemes[theme.id]
            const badge = getScoreBadge(themeAverage)

            return (
              <div 
                key={theme.id} 
                className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Theme Header - Clickable */}
                <button
                  onClick={() => toggleTheme(theme.id)}
                  className="w-full flex items-center justify-between p-5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <div className="flex items-center flex-1">
                    <span className="text-3xl mr-4">{theme.icon}</span>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">
                        {theme.title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {theme.questions?.length || 0} questions
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Score Badge */}
                    <div className="text-right">
                      <div className={`inline-flex px-4 py-2 text-lg font-bold rounded-lg ${getScoreColor(themeAverage)}`}>
                        {themeAverage.toFixed(1)}/10
                      </div>
                      <div className={`mt-1 inline-block px-3 py-1 rounded-full text-xs font-semibold ${badge.color} text-white ml-2`}>
                        {badge.text}
                      </div>
                    </div>

                    {/* Expand Icon */}
                    <div className="text-gray-400">
                      {isExpanded ? (
                        <ChevronUp className="w-6 h-6" />
                      ) : (
                        <ChevronDown className="w-6 h-6" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Expanded Questions */}
                {isExpanded && (
                  <div className="p-5 bg-white border-t border-gray-200">
                    <div className="space-y-3">
                      {theme.questions?.map((question) => {
                        const score = assessmentData.scores[question.id]

                        return (
                          <div 
                            key={question.id} 
                            className="bg-gray-50 rounded-lg p-4 border-l-4"
                            style={{ 
                              borderLeftColor: score >= 7 ? '#10b981' : score >= 5 ? '#f59e0b' : '#ef4444' 
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 pr-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-2">
                                  {question.title}
                                </h5>
                                <p className="text-xs text-gray-600 leading-relaxed">
                                  {question.description}
                                </p>
                              </div>

                              <div className="flex-shrink-0">
                                {score !== undefined && score !== null ? (
                                  <div className="text-center">
                                    <div className={`inline-flex px-3 py-2 text-sm font-bold rounded-lg ${getScoreColor(score)}`}>
                                      {score}/10
                                    </div>
                                  </div>
                                ) : (
                                  <span className="inline-flex px-3 py-1 text-xs text-gray-500 bg-gray-200 rounded">
                                    Non répondu
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    {/* Theme Summary */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">
                          Questions répondues : {theme.questions.filter(q => assessmentData.scores[q.id] !== null && assessmentData.scores[q.id] !== undefined).length}/{theme.questions.length}
                        </span>
                        <span className={`font-semibold ${themeAverage >= 7 ? 'text-green-600' : themeAverage >= 5 ? 'text-yellow-600' : 'text-red-600'}`}>
                          Moyenne du thème : {themeAverage.toFixed(1)}/10
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-cedec-blue bg-opacity-10 rounded-xl p-6 text-center border-2 border-cedec-blue">
        <h3 className="text-lg font-semibold text-cedec-blue mb-2">
          🎯 Prochaines étapes
        </h3>
        <p className="text-gray-700 text-sm mb-4">
          Faire le point avec votre consultant CEDEC pour discuter de vos résultats.
        </p>
      </div>
    </div>
  )
}

export default AssessmentSummary

